/**
 * Quantity stepper — increments/decrements an input[name=quantity].
 * Supports inventory max, B2B catalog min / max / increment and the store's
 * per-item basket limit (theme setting).
 */
(() => {
  const TAG = 'quantity-selector';

  const toInt = (value, fallback) => {
    const next = Number(value);

    if (Number.isNaN(next)) return fallback;

    return Math.floor(next);
  };

  // Store-wide per-item cap from the theme setting. It mirrors a basket limit
  // the storefront cannot read, so every selector renders the same value.
  const storeMax = () => {
    const value = document.querySelector('[data-store-max]')?.dataset.storeMax;

    if (value == null || value === '') return null;

    const next = Math.floor(Number(value));

    return Number.isNaN(next) || next < 1 ? null : next;
  };

  window.themeQuantityLimitsForVariant = (variant) => {
    // Without a variant there is nothing to derive from, and neutral defaults
    // would wipe the catalog rules Liquid already rendered on single-variant
    // products, which have no picker to read.
    if (!variant) return null;

    const rule = variant.quantity_rule || {};
    const min = Math.max(1, toInt(rule.min, 1));
    const increment = Math.max(1, toInt(rule.increment, 1));
    const catalogMax =
      rule.max == null || rule.max === ''
        ? null
        : Math.max(0, Math.floor(Number(rule.max)));

    let inventoryMax = null;

    const tracked = Boolean(variant.inventory_management);
    const continueSelling = variant.inventory_policy === 'continue';

    if (tracked && !continueSelling) {
      const qty = Number(variant.inventory_quantity || 0);

      if (!variant.available || qty < 1) {
        inventoryMax = 0;
      } else {
        inventoryMax = Math.max(0, qty);
      }
    }

    let max = inventoryMax;

    if (catalogMax != null) {
      max = max == null ? catalogMax : Math.min(max, catalogMax);
    }

    const storeCap = storeMax();

    if (storeCap != null) {
      max = max == null ? storeCap : Math.min(max, storeCap);
    }

    return { min, max, increment, catalogMax };
  };

  /**
   * Catalog rules can outrun stock — a minimum of 4 against 2 in stock leaves no
   * quantity the cart will accept. Keep this aligned with the Liquid
   * `variant-has-purchasable-quantity` snippet used before custom elements load.
   */
  window.themeHasPurchasableQuantity = (variant) => {
    const limits = window.themeQuantityLimitsForVariant(variant);

    if (!limits || limits.max == null) return true;

    return limits.min <= limits.max;
  };

  if (customElements.get(TAG)) return;

  class QuantitySelector extends HTMLElement {
    #min = 1;
    #max = null;
    #increment = 1;
    #catalogMax = null;

    connectedCallback() {
      this.#readLimitsFromDom();
      this.#decrease()?.addEventListener('click', this.#onDecrease);
      this.#increase()?.addEventListener('click', this.#onIncrease);
      this.#input()?.addEventListener('change', this.#onChange);
      this.#clampCurrent();
      this.#syncButtons();
    }

    disconnectedCallback() {
      this.#decrease()?.removeEventListener('click', this.#onDecrease);
      this.#increase()?.removeEventListener('click', this.#onIncrease);
      this.#input()?.removeEventListener('change', this.#onChange);
    }

    /**
     * @param {number | null | undefined} max - Inventory / catalog cap, or null for none
     */
    setMax(max) {
      this.setLimits({ min: this.#min, max, increment: this.#increment });
    }

    /**
     * @param {{ min?: number, max?: number | null, increment?: number, catalogMax?: number | null }} limits
     */
    setLimits(limits = {}) {
      const min = Math.max(1, toInt(limits.min, this.#min));
      const increment = Math.max(1, toInt(limits.increment, this.#increment));
      let max = this.#max;

      if (Object.prototype.hasOwnProperty.call(limits, 'max')) {
        if (limits.max == null || limits.max === '' || Number.isNaN(Number(limits.max))) {
          max = null;
        } else {
          max = Math.max(0, Math.floor(Number(limits.max)));
        }
      }

      if (Object.prototype.hasOwnProperty.call(limits, 'catalogMax')) {
        if (
          limits.catalogMax == null ||
          limits.catalogMax === '' ||
          Number.isNaN(Number(limits.catalogMax))
        ) {
          this.#catalogMax = null;
        } else {
          this.#catalogMax = Math.max(0, Math.floor(Number(limits.catalogMax)));
        }
      }

      this.#min = min;
      this.#increment = increment;
      this.#max = max;

      this.dataset.min = String(this.#min);
      this.dataset.increment = String(this.#increment);

      if (this.#catalogMax == null) {
        delete this.dataset.catalogMax;
      } else {
        this.dataset.catalogMax = String(this.#catalogMax);
      }

      const input = this.#input();

      if (input) {
        input.min = String(this.#min);
        input.step = String(this.#increment);
      }

      if (this.#max == null) {
        delete this.dataset.max;
        if (input) input.removeAttribute('max');
      } else {
        this.dataset.max = String(this.#max);
        if (input) input.max = String(this.#max);
      }

      this.#clampCurrent();
      this.#syncButtons();
    }

    /**
     * @returns {string | null} Translated rule error, or null when the value is valid
     */
    getRuleError() {
      if (this.#max === 0) return null;

      const value = this.#value();

      if (value < this.#min) {
        return this.#ruleError('min', this.#min);
      }

      if (this.#max != null && value > this.#max) {
        if (this.#catalogMax != null && this.#max === this.#catalogMax) {
          return this.#ruleError('max', this.#catalogMax);
        }

        return null;
      }

      if ((value - this.#min) % this.#increment !== 0) {
        return (
          this.#ruleError('increment', this.#increment) ||
          this.#ruleError('min', this.#min)
        );
      }

      return null;
    }

    #readLimitsFromDom() {
      this.#min = Math.max(1, toInt(this.dataset.min, 1));
      this.#increment = Math.max(1, toInt(this.dataset.increment, 1));
      this.#catalogMax =
        this.dataset.catalogMax == null || this.dataset.catalogMax === ''
          ? null
          : Math.max(0, Math.floor(Number(this.dataset.catalogMax)));

      if (this.dataset.max == null || this.dataset.max === '') {
        this.#max = null;
        return;
      }

      this.#max = Math.max(0, Math.floor(Number(this.dataset.max)));
    }

    #ruleError(type, value) {
      const template = this.dataset[`${type}ErrorTemplate`] || '';

      return template
        ? template.split('__value__').join(String(value))
        : null;
    }

    #input() {
      return this.querySelector('[data-quantity-input]');
    }

    #decrease() {
      return this.querySelector('[data-quantity-decrease]');
    }

    #increase() {
      return this.querySelector('[data-quantity-increase]');
    }

    #value() {
      return Number(this.#input()?.value || this.#min);
    }

    #snap(next) {
      let snapped = Math.max(this.#min, Math.floor(Number(next) || this.#min));
      const offset = snapped - this.#min;
      const remainder = offset % this.#increment;

      if (remainder !== 0) {
        snapped = this.#min + Math.round(offset / this.#increment) * this.#increment;
      }

      if (this.#max != null) {
        snapped = Math.min(snapped, this.#max);

        // A max off the min/increment grid (stock of 10 against a case pack of
        // 4) would otherwise leave a value add to cart rejects, so step back to
        // the highest quantity the rule allows.
        const clamped = (snapped - this.#min) % this.#increment;

        if (clamped !== 0) {
          snapped -= clamped;
        }
      }

      if (this.#max === 0) {
        snapped = this.#min;
      }

      if (snapped < this.#min) {
        snapped = this.#min;
      }

      return snapped;
    }

    #setValue(next) {
      const input = this.#input();

      if (!input) return;

      const nextValue = String(this.#snap(next));

      if (input.value === nextValue) {
        this.#syncButtons();
        return;
      }

      input.value = nextValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.#syncButtons();
    }

    #clampCurrent() {
      this.#setValue(this.#value());
    }

    #syncButtons() {
      const decrease = this.#decrease();
      const increase = this.#increase();
      const value = this.#value();

      if (decrease) {
        decrease.disabled = value <= this.#min || this.#max === 0;
      }

      if (increase) {
        const next = value + this.#increment;

        increase.disabled =
          this.#max != null && (this.#max === 0 || next > this.#max);
      }
    }

    #onDecrease = () => {
      this.#setValue(this.#value() - this.#increment);
    };

    #onIncrease = () => {
      this.#setValue(this.#value() + this.#increment);
    };

    #onChange = () => {
      this.#setValue(this.#value());
    };
  }

  customElements.define(TAG, QuantitySelector);
})();
